import { assert, copyObjectWithoutKeys, type NullableValues, objectEntries, type ReadonlyRecord } from '../../../utils'

import {
    type AppState,
    areServiceParametersEquals,
    ConnectwareError,
    ConnectwareErrorType,
    type CybusDetailedService,
    type CybusServiceForm,
    type CybusServiceParameters,
    type CybusServiceParameterValue,
    type CybusServiceRawParameters,
    getSecretParameterNames,
    isAddOrUpdateServiceCommissioningFileValid,
    isAddOrUpdateServiceSchemaValid,
    selectServiceForm,
} from '../../../domain'

import { ServiceManagementMode, type ServiceManagementRequest } from '../..'
import { CommissioningFileUsecase } from './Commissioning'

const initial: Omit<CybusServiceForm, 'creating'> = {
    file: null,
    schema: null,
    id: null,
    parameters: null,
    requesting: false,
    initialFormValue: null,
}

const catchPromise = (e: ConnectwareError): ConnectwareError => e

/**
 * Copies the entries of `parameters` whose value `keep` accepts, dropping the rest, in one linear
 * pass — as opposed to a `reduce` that spreads into a fresh object on every iteration
 */
const filterParameterEntries = <Value, Kept extends Value>(
    parameters: ReadonlyRecord<string, Value>,
    keep: (value: Value) => value is Kept
): ReadonlyRecord<string, Kept> => Object.fromEntries(objectEntries(parameters).filter((entry): entry is [string, Kept] => keep(entry[1])))

const isDefinedParameterValue = (value: CybusServiceParameterValue | undefined): value is CybusServiceParameterValue => value !== undefined

const isNotNullParameterValue = (value: CybusServiceParameterValue | null): value is CybusServiceParameterValue => value !== null

/**
 * Drops keys whose value is undefined — @rjsf widgets emit undefined when a
 * field is cleared, and an absent secret parameter means "keep the stored value"
 */
const dropUndefinedParameters = (parameters: CybusServiceRawParameters): CybusServiceParameters => filterParameterEntries(parameters, isDefinedParameterValue)

abstract class BaseUsecase extends CommissioningFileUsecase {
    private getNullableServiceForm (): CybusServiceForm | null {
        return selectServiceForm(this.getState())
    }

    private getRequest (): ServiceManagementRequest | null {
        const form = this.getNullableServiceForm()

        if (
            form === null ||
            form.requesting === true ||
            !form.id ||
            !form.parameters ||
            !isAddOrUpdateServiceCommissioningFileValid(form.file) ||
            !isAddOrUpdateServiceSchemaValid(form.schema) ||
            (form.initialFormValue &&
                form.id === form.initialFormValue.id &&
                form.file === form.initialFormValue.file &&
                areServiceParametersEquals(form.parameters, form.initialFormValue.parameters)) ||
            !this.connectwareServicesService.validate({ schema: form.schema, id: form.id, parameters: form.parameters }, this.getServiceManagementMode())
        ) {
            return null
        }

        return { commissioningFile: form.file, id: form.id, parameters: form.parameters }
    }

    private getServiceFormFile (): CybusServiceForm['file'] {
        return this.getServiceForm().file
    }

    private isCreating (): boolean {
        return this.getServiceManagementMode() === ServiceManagementMode.CREATION
    }

    protected initializeServiceForm (serviceForm: AppState['serviceForm']): void {
        this.setState({ serviceForm })
    }

    protected getServiceForm (): CybusServiceForm {
        const form = this.getNullableServiceForm()
        assert(form, new ConnectwareError(ConnectwareErrorType.STATE, 'Could not update service creation form'))
        return form
    }

    protected setServiceForm (form: Partial<CybusServiceForm>): void {
        this.initializeServiceForm({ ...this.getServiceForm(), ...form })
    }

    protected close (): void {
        this.initializeServiceForm(null)
    }

    protected createParameters (
        parameters: CybusServiceParameters | null,
        initialParameters: NullableValues<CybusServiceParameters> | null
    ): CybusServiceParameters | null {
        const out = parameters || initialParameters

        return out && filterParameterEntries(out, isNotNullParameterValue)
    }

    /**
     * Merges the initial parameters with the parameters of the service
     *
     * @param current the parameters of the service
     * @param initial the initial parameters of the service
     *
     * @returns valid parameters of the service, correct for any changes between a version of a service
     */
    protected mergeParameters (current: CybusServiceParameters, initial: NullableValues<CybusServiceParameters>): CybusServiceParameters {
        return objectEntries(initial).reduce<CybusServiceParameters>((outParameters, [parameterKey, initialValue]) => {
            const currentValue = current[parameterKey]

            if (initialValue === null) {
                if (currentValue === undefined) {
                    /** There is no value there, so just send it as is */
                    return outParameters
                }

                /** The value is set already, so use it */
                return { ...outParameters, [parameterKey]: currentValue }
            }

            if (currentValue !== undefined && typeof currentValue === typeof initialValue) {
                /** The value is set already and the value is of the same type, so use it */
                return { ...outParameters, [parameterKey]: currentValue }
            }

            /** Use the initial value */
            return { ...outParameters, [parameterKey]: initialValue }
        }, {})
    }

    abstract getServiceManagementMode (): ServiceManagementMode

    /**
     * Update the commissioning file for the service that will be created
     * Resets current service schema/parameters in some scenarios
     */
    async updateCommissioningFile (newFile: CybusServiceForm['file']): Promise<void> {
        const oldFile = this.getServiceFormFile()

        if (oldFile === newFile) {
            /** If the new file is the same, then do nothing */
            return
        }

        if (!isAddOrUpdateServiceCommissioningFileValid(newFile)) {
            this.setServiceForm({ file: newFile, schema: null, id: null, parameters: null })
            return
        }

        const { parameters } = this.getServiceForm()

        /** Clear derived information and start loading */
        this.setServiceForm({ file: newFile, schema: null, parameters: null, requesting: true })

        /** Commissioning file was read, let's try to parse it */
        const schemaResolution = await this.connectwareServicesService
            .parseCommissioningFile(newFile, this.getServiceManagementMode())
            .then(([schema, id, parameters]) => ({ schema, id: this.isCreating() ? id : this.getServiceForm().id, parameters }))
            .catch((e: ConnectwareError) => ({ schema: e, id: null, parameters: null }))

        if (this.getServiceFormFile() !== newFile) {
            /** File has changed since parsing started, so stop the presses and let the last process do its thing */
            return
        }

        /** Set state when promise yields */
        let newParameters = schemaResolution.parameters === null || parameters === null
            ? this.createParameters(parameters, schemaResolution.parameters)
            : this.mergeParameters(parameters, schemaResolution.parameters)

        if (newParameters && isAddOrUpdateServiceSchemaValid(schemaResolution.schema)) {
            /**
             * Secret (write-only) parameters never carry a value in the form — on update the API
             * omits them (see startUpdate), and on creation a schema `default` is not a value the
             * user actually typed, so it must not be echoed back into the write-only widget either
             */
            newParameters = copyObjectWithoutKeys(newParameters, ...getSecretParameterNames(schemaResolution.schema))
        }

        this.setServiceForm({
            ...schemaResolution,
            file: newFile,
            parameters: newParameters,
            id: this.isCreating() ? schemaResolution.id : this.getServiceForm().id,
            requesting: false,
        })
    }

    updateServiceParameters (id: CybusServiceForm['id'], parameters: CybusServiceRawParameters | null): void {
        const { schema, id: previousId } = this.getServiceForm()
        assert(isAddOrUpdateServiceSchemaValid(schema), new ConnectwareError(ConnectwareErrorType.STATE, 'Could not update service parameters'))
        this.setServiceForm({ id: this.isCreating() ? id : previousId, parameters: parameters && dropUndefinedParameters(parameters) })
    }

    cancel (): void {
        this.close()
    }

    async confirm (): Promise<string | null> {
        const args = this.getRequest()

        assert(args, new ConnectwareError(ConnectwareErrorType.STATE, 'Cannot create or update the service'))

        const { id } = args

        this.setServiceForm({ requesting: true })

        return (
            this.connectwareServicesService
                .manage(args, this.getServiceManagementMode())
                .then(() => {
                    this.close()

                    return id
                })
                /** Record the error */
                .catch((requesting: ConnectwareError) => {
                    this.setServiceForm({ requesting })

                    return null
                })
        )
    }

    isConfirmationRequestValid (): boolean {
        return Boolean(this.getRequest())
    }
}

export class AddServiceUsecase extends BaseUsecase {
    override getServiceManagementMode (): ServiceManagementMode {
        return ServiceManagementMode.CREATION
    }

    startCreation (): void {
        this.initializeServiceForm({ ...initial, creating: true })
    }
}

export class UpdateServiceUsecase extends BaseUsecase {
    /**
     * The update form is only ready after an async fetch, by which time a creation form may have been opened, and it must not be replaced
     */
    private initializeUpdateForm (form: Omit<CybusServiceForm, 'creating'>): void {
        if (selectServiceForm(this.getState())?.creating) {
            return
        }

        this.initializeServiceForm({ ...form, creating: false })
    }

    override getServiceManagementMode (): ServiceManagementMode {
        return ServiceManagementMode.UPDATE
    }

    async startUpdate ({ id, parameters }: CybusDetailedService): Promise<void> {
        const commissioningFile = await this.connectwareServicesService.fetchCommissioningFile(id)
        const parsedResponse = await this.connectwareServicesService.parseCommissioningFile(commissioningFile, ServiceManagementMode.UPDATE).catch(catchPromise)

        if (ConnectwareError.is(parsedResponse)) {
            this.initializeUpdateForm({ ...initial, schema: parsedResponse })
            return
        }

        const [schema, , initialParameters] = parsedResponse

        /**
         * Secret (write-only) parameters never carry a value in the update form:
         * the API omits them, and a schema default must not silently overwrite the stored value
         */
        const outParameters = copyObjectWithoutKeys(this.mergeParameters(parameters, initialParameters), ...getSecretParameterNames(schema))

        this.initializeUpdateForm({
            file: commissioningFile,
            schema,
            id,
            parameters: outParameters,
            requesting: false,
            initialFormValue: { id, file: commissioningFile, parameters: outParameters },
        })
    }
}
